import React, { useState } from 'react';
import { faClockRotateLeft, faEllipsisVertical, faFilter } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { WidgetContainer } from '@/app/refresh-components';
import { cn } from '@/shadcn/lib/utils';
import { Skeleton } from '@/shadcn/components/ui/skeleton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/shadcn/components/ui/dropdown-menu';
import { Button } from '@/shadcn/components/ui/button';
import Empty from '@/app/refresh-components/Empty';
import { DateRangeForm, DateRangeFormData } from '@/app/refresh-components';
import { ActivityTimeline } from './ActivityTimeline';
import { useActivityData } from './useActivityData';
import { FILTER_OPTIONS } from './constants';

interface ProgramActivityProps {
  projectId: number;
  className?: string;
}

// Widget configuration constants
const WIDGET_CONFIG = {
  title: 'Activity',
  icon: faClockRotateLeft,
  iconClass: 'text-grey-4',
  textColor: 'text-grey-6 text-sm',
  bgColor: 'bg-white py-2',
  containerClass: 'h-full',
  maxHeight: 'max-h-full',
  skeletonCount: 6,
} as const;

// Skeleton component for loading state
const ActivityItemSkeleton: React.FC = () => (
  <div className="flex items-start gap-3 p-3 border-b border-gray-100 last:border-b-0">
    <Skeleton className="w-8 h-8 rounded-full flex-shrink-0" />
    <div className="flex-1 space-y-2">
      <div className="flex items-center justify-between">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-16" />
      </div>
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-3/4" />
    </div>
  </div>
);

// Loading skeleton for the entire widget
const ActivityLoadingSkeleton: React.FC = () => (
  <div className="space-y-0">
    {Array.from({ length: WIDGET_CONFIG.skeletonCount }).map((_, index) => (
      <ActivityItemSkeleton key={index} />
    ))}
  </div>
);

export const ProgramActivity = ({ projectId, className }: ProgramActivityProps) => {
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateRangeData, setDateRangeData] = useState<DateRangeFormData>({
    dateType: 'thisWeek',
    dateRange: undefined,
  });
  const { data: activities, loading, error, refetch } = useActivityData(projectId, dateRangeData, typeFilter);

  // Handle date range changes
  const handleDateRangeChange = (newDateRangeData: DateRangeFormData) => {
    setDateRangeData(newDateRangeData);
  };

  // Create filter components for widget actions
  const FilterActions = () => (
    <div className="flex items-center gap-2">
      <DateRangeForm
        onApply={handleDateRangeChange}
        defaultValues={dateRangeData}
        iconPlaceholder
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 w-8 p-0">
            <FontAwesomeIcon icon={faFilter} className="text-grey-6 text-xs" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {FILTER_OPTIONS.map((option) => (
            <DropdownMenuItem
              key={option.value}
              onClick={() => setTypeFilter(option.value)}
              className={typeFilter === option.value ? 'bg-blue-50 text-blue-600' : ''}
            >
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <FontAwesomeIcon icon={faEllipsisVertical} className="text-grey-6" />
    </div>
  );

  // Reusable widget renderer
  const renderWidget = (children: React.ReactNode) => (
    <WidgetContainer
      widgetTitle={WIDGET_CONFIG.title}
      textColor={WIDGET_CONFIG.textColor}
      bgColor={WIDGET_CONFIG.bgColor}
      widgetIcon={<FontAwesomeIcon icon={WIDGET_CONFIG.icon} className={WIDGET_CONFIG.iconClass} />}
      widgetActions={<FilterActions />}
      className={cn(WIDGET_CONFIG.containerClass, className)}
    >
      {children}
    </WidgetContainer>
  );

  // Render loading state
  if (loading) {
    return renderWidget(<ActivityLoadingSkeleton />);
  }

  // Render error state
  if (error) {
    return renderWidget(
      <div className="py-8">
        <Empty
          title="Failed to load activity"
          description="There was an error loading the program activity. Please try refreshing the page."
          size="md"
          type="error"
        />
      </div>
    );
  }

  // Render empty state
  if (activities.length === 0) {
    const hasFilters = dateRangeData.dateType !== 'allTime' || typeFilter !== 'all';
    const emptyMessage = hasFilters
      ? {
          title: 'No activity found',
          description: 'No activity matches your current filters. Try adjusting your filter criteria.',
        }
      : {
          title: 'No campaign activity yet',
          description: 'Activity from your campaigns will appear here. If you\'re experiencing no activity, try adjusting your filters.',
        };

    return renderWidget(
      <div className="py-8">
        <Empty title={emptyMessage.title} description={emptyMessage.description} size="md" />
      </div>
    );
  }

  // Render activities
  return renderWidget(
    <div className={`${WIDGET_CONFIG.maxHeight} overflow-y-auto`}>
      <ActivityTimeline projectId={projectId} activities={activities} refetchActivities={refetch} />
    </div>
  );
};
